import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { Autocomplete, CircularProgress, TextField } from '@mui/material'; // ✅ NEW
import { GetSalesPersonList } from '../../services';
import notistack from 'modules/core/notistack';

interface SalesPersonModalProps {
    show: boolean;
    onHide: () => void;
    onSelect?: (salesPersonId: string) => void;
    selectedSalesPersonId?: string;
}

interface SalesPersonOption {
    value?: string | number;
    text?: string;
    id?: string | number;
    name?: string;
    [key: string]: any;
}

const SalesPersonModal: React.FC<SalesPersonModalProps> = ({
    show,
    onHide,
    onSelect,
    selectedSalesPersonId = '',
}) => {
    const [salesPersonList, setSalesPersonList] = useState<SalesPersonOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedOption, setSelectedOption] = useState<SalesPersonOption | null>(null);

    // Load list when modal opens
    useEffect(() => {
        if (show) {
            getAllSalesPersonList();
        }
    }, [show]);

    // Sync selected option when list or selectedSalesPersonId changes
    useEffect(() => {
        if (!show) return;

        if (!selectedSalesPersonId) {
            setSelectedOption(null);
            return;
        }

        const found = salesPersonList.find(
            (sp) =>
                String(sp.value) === String(selectedSalesPersonId) ||
                String(sp.id) === String(selectedSalesPersonId)
        ) || null;

        setSelectedOption(found);
    }, [show, selectedSalesPersonId, salesPersonList]);

    const getAllSalesPersonList = async () => {
        try {
            setLoading(true);
            const response = await GetSalesPersonList();
            setSalesPersonList(response || []);
        } catch (error: any) {
            setSalesPersonList([]);
            notistack.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (
        _event: React.SyntheticEvent,
        newValue: SalesPersonOption | null
    ) => {
        setSelectedOption(newValue);

        if (onSelect) {
            // If cleared, send empty string; else send id/value as string
            const id =
                newValue?.value ?? newValue?.id ?? '';
            onSelect(id !== undefined && id !== null ? String(id) : '');
        }

        onHide();
    };

    const handleModalHide = () => {
        onHide();
    };

    return (
        <Modal
            show={show}
            size="sm"
            onHide={handleModalHide}
            className="fade"
            dialogClassName="modal-dialog-centered"
            backdrop="static"
        >
            <Modal.Header closeButton className="border-0 pb-2">
                <div className="d-flex align-items-center">
                    <i className="bx bx-user-check me-2"></i>
                    <span className="fw-bold">Select Sales Person</span>
                </div>
            </Modal.Header>

            <Modal.Body className="pt-0">
                <div className="mb-4">
                    <Autocomplete
                        disablePortal
                        options={salesPersonList}
                        value={selectedOption}
                        onChange={handleChange}
                        getOptionLabel={(option: any) =>
                            option?.text || option?.name || '[No Name]'
                        }
                        isOptionEqualToValue={(option, value) =>
                            String(option.value ?? option.id) ===
                            String(value.value ?? value.id)
                        }
                        loading={loading}
                        onOpen={() => {
                            if (!salesPersonList.length) {
                                getAllSalesPersonList();
                            }
                        }}
                        size="small"
                        renderOption={(props, option: any) => (
                            <li {...props} key={option?.value || option?.id}>
                                <div className="d-flex align-items-center w-100">
                                    <i className="bx bx-user me-2 text-muted"></i>
                                    <div className="flex-grow-1">
                                        <div className="fw-medium" style={{ color: '#000' }}>
                                            {option?.text || option?.name || '[No Name]'}
                                        </div>
                                        {option?.department && (
                                            <small className="text-muted">
                                                {option.department}
                                            </small>
                                        )}
                                    </div>
                                </div>
                            </li>
                        )}
                        sx={{
                            width: '100%',
                            '& .MuiInputBase-root': {
                                fontSize: '0.875rem',
                                color: '#000',
                            },
                            '& .MuiInputLabel-root': {
                                fontSize: '0.875rem',
                                color: '#000',
                            },
                            '& .MuiAutocomplete-listbox': {
                                fontSize: '0.875rem',
                                padding: '4px 0',
                            },
                            '& .MuiAutocomplete-option': {
                                fontSize: '0.875rem',
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={
                                    <>
                                        Sales Person
                                        <span className="text-danger"> *</span>
                                    </>
                                }
                                size="small"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loading ? (
                                                <CircularProgress color="inherit" size={20} />
                                            ) : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </div>

                <div className="d-flex justify-content-end gap-2">
                    <button
                        type="button"
                        className="btn add-btn btn-sm px-4"
                        onClick={handleModalHide}
                    >
                        Cancel
                    </button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default SalesPersonModal;